/**
 * GridAddressing.ts
 * Cell type detection, neighbor queries, and world-space positioning
 * for the octile (truncated square) tiling grid.
 *
 * CORRECTED geometry: In a truncated square tiling:
 *   - Octagons share LONG edges with adjacent octagons (N, E, S, W)
 *   - Squares fill DIAGONAL gaps between 4 octagons (NE, SE, SW, NW corners)
 *   - Squares appear as DIAMONDS rotated 45°
 *
 * Grid addressing (19×19 for 10×10 octagons):
 *   even,even = octagon  |  odd,odd = connector (diamond)  |  mixed = void
 */

import type { GridCellKind, GridPos } from '../types/OctileTypes.ts'
import { GRID_DEFAULTS } from '../types/OctileTypes.ts'

// ─── Cell Type Detection ────────────────────────────────────

/**
 * Determine what kind of cell exists at the given grid position.
 *
 * Octagons at even,even: (0,0), (0,2), (2,0), (2,2) ...
 * Connectors at odd,odd: (1,1), (1,3), (3,1), (3,3) ... (diagonal gaps)
 * Void at mixed parity: (0,1), (1,0), (1,2) ... (nothing here)
 */
export function getCellType(row: number, col: number): GridCellKind {
    const rowEven = row % 2 === 0
    const colEven = col % 2 === 0

    if (rowEven && colEven) return 'octagon'
    if (!rowEven && !colEven) return 'connector'  // odd,odd = diagonal gap
    return 'void'  // mixed parity = nothing
}

/**
 * Check if a grid position is within bounds.
 */
export function inBounds(row: number, col: number, gridWidth: number, gridHeight: number): boolean {
    return row >= 0 && row < gridHeight && col >= 0 && col < gridWidth
}

// ─── Interleaved Grid Dimensions ────────────────────────────

/**
 * Calculate the interleaved grid dimensions for a given octagon count.
 * A 10×10 octagon grid becomes a 19×19 interleaved grid.
 */
export function getGridDimensions(octCols: number, octRows: number): { width: number; height: number } {
    return {
        width: octCols * 2 - 1,
        height: octRows * 2 - 1,
    }
}

// ─── Neighbor Queries ───────────────────────────────────────

/**
 * Get all valid neighbors of an octagonal cell.
 *
 * Each octagon has 8 neighbors:
 *   4 octagons via shared LONG edges: N(r-2,c), E(r,c+2), S(r+2,c), W(r,c-2)
 *   4 connectors via shared SHORT edges (cut corners):
 *     NE(r-1,c+1), SE(r+1,c+1), SW(r+1,c-1), NW(r-1,c-1)
 */
export function getOctagonNeighbors(
    row: number, col: number,
    gridWidth: number, gridHeight: number
): GridPos[] {
    const neighbors: GridPos[] = []

    // 4 adjacent octagons via long edges (cardinal directions)
    const octagonOffsets: GridPos[] = [
        { row: row - 2, col },       // N
        { row, col: col + 2 },       // E
        { row: row + 2, col },       // S
        { row, col: col - 2 },       // W
    ]

    // 4 connector diamonds via short edges (diagonal cut corners)
    const connectorOffsets: GridPos[] = [
        { row: row - 1, col: col + 1 },  // NE
        { row: row + 1, col: col + 1 },  // SE
        { row: row + 1, col: col - 1 },  // SW
        { row: row - 1, col: col - 1 },  // NW
    ]

    for (const pos of [...octagonOffsets, ...connectorOffsets]) {
        if (inBounds(pos.row, pos.col, gridWidth, gridHeight)) {
            neighbors.push(pos)
        }
    }

    return neighbors
}

/**
 * Get all valid neighbors of a square connector (diamond).
 *
 * Each connector at (r,c) where both are odd touches exactly 4 octagons:
 *   (r-1,c-1), (r-1,c+1), (r+1,c-1), (r+1,c+1)
 */
export function getConnectorNeighbors(
    row: number, col: number,
    gridWidth: number, gridHeight: number
): GridPos[] {
    const neighbors: GridPos[] = []

    const offsets: GridPos[] = [
        { row: row - 1, col: col - 1 },  // top-left octagon
        { row: row - 1, col: col + 1 },  // top-right octagon
        { row: row + 1, col: col + 1 },  // bottom-right octagon
        { row: row + 1, col: col - 1 },  // bottom-left octagon
    ]

    for (const pos of offsets) {
        if (inBounds(pos.row, pos.col, gridWidth, gridHeight) &&
            getCellType(pos.row, pos.col) === 'octagon') {
            neighbors.push(pos)
        }
    }

    return neighbors
}

/**
 * Get neighbors for any cell type.
 */
export function getNeighbors(
    row: number, col: number,
    gridWidth: number, gridHeight: number
): GridPos[] {
    const kind = getCellType(row, col)
    if (kind === 'octagon') return getOctagonNeighbors(row, col, gridWidth, gridHeight)
    if (kind === 'connector') return getConnectorNeighbors(row, col, gridWidth, gridHeight)
    return []
}

// ─── World-Space Position ───────────────────────────────────

/**
 * Convert grid coordinates to world-space position.
 *
 * Octagons tile on a regular grid with spacing = baseSquareSize.
 * Connectors sit at the diagonal midpoints between 4 octagons.
 */
export function gridToWorld(
    row: number, col: number,
    baseSquareSize: number = GRID_DEFAULTS.baseSquareSize,
    _cutRatio: number = GRID_DEFAULTS.cornerCutRatio
): { x: number; y: number; z: number } {
    // In the truncated square tiling, octagons are spaced by baseSquareSize.
    // Grid coords step by 2 for octagons, so actual spacing = baseSquareSize / 2 per grid unit.
    const unit = baseSquareSize / 2

    return {
        x: col * unit,
        y: 0,
        z: row * unit,
    }
}

/**
 * Get the world-space dimensions of a cell.
 */
export function getCellWorldSize(
    kind: GridCellKind,
    baseSquareSize: number = GRID_DEFAULTS.baseSquareSize,
    cutRatio: number = GRID_DEFAULTS.cornerCutRatio
): { width: number; depth: number } {
    if (kind === 'octagon') {
        return { width: baseSquareSize, depth: baseSquareSize }
    }
    const connSize = baseSquareSize * cutRatio * Math.SQRT2
    return { width: connSize, depth: connSize }
}

// ─── Wall Index Mapping ─────────────────────────────────────

/**
 * Octagon wall indices (clockwise from N):
 *   0=N (long, shared with N octagon)
 *   1=NE (short, shared with NE connector diamond)
 *   2=E (long, shared with E octagon)
 *   3=SE (short, shared with SE connector diamond)
 *   4=S (long, shared with S octagon)
 *   5=SW (short, shared with SW connector diamond)
 *   6=W (long, shared with W octagon)
 *   7=NW (short, shared with NW connector diamond)
 */
export const OCT_WALL_NAMES = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const

/**
 * Get the octagon wall index that faces a given neighbor.
 * Returns -1 if the positions aren't adjacent.
 */
export function getOctWallToward(fromRow: number, fromCol: number, toRow: number, toCol: number): number {
    const dr = toRow - fromRow
    const dc = toCol - fromCol

    if (dr === -2 && dc === 0) return 0   // N octagon (long edge)
    if (dr === -1 && dc === 1) return 1   // NE connector (short edge)
    if (dr === 0 && dc === 2) return 2    // E octagon (long edge)
    if (dr === 1 && dc === 1) return 3    // SE connector (short edge)
    if (dr === 2 && dc === 0) return 4    // S octagon (long edge)
    if (dr === 1 && dc === -1) return 5   // SW connector (short edge)
    if (dr === 0 && dc === -2) return 6   // W octagon (long edge)
    if (dr === -1 && dc === -1) return 7  // NW connector (short edge)
    return -1
}

/**
 * Get the connector wall index that faces a given octagon.
 * Connector walls: 0=top-left, 1=top-right, 2=bottom-right, 3=bottom-left
 */
export function getConnWallToward(fromRow: number, fromCol: number, toRow: number, toCol: number): number {
    const dr = toRow - fromRow
    const dc = toCol - fromCol

    if (dr === -1 && dc === -1) return 0  // top-left octagon
    if (dr === -1 && dc === 1) return 1   // top-right octagon
    if (dr === 1 && dc === 1) return 2    // bottom-right octagon
    if (dr === 1 && dc === -1) return 3   // bottom-left octagon
    return -1
}

// ─── Grid Key ───────────────────────────────────────────────

export function gridKey(row: number, col: number): string {
    return `${row},${col}`
}

export function parseGridKey(key: string): GridPos {
    const [row, col] = key.split(',').map(Number)
    return { row, col }
}
